/**
 * @fileoverview HTML Pre/Post Processing for Template Editor
 *
 * Purpose: Round-trip conversion between the stored {{field_name}} placeholder
 * format and the TipTap-compatible `<span data-type="field-placeholder">` nodes.
 *
 * - prepareHtmlForEditor: DB → Editor (wraps placeholders in span nodes)
 * - extractHtmlFromEditor: Editor → DB (unwraps span nodes back to {{key}})
 */

import { TEMPLATE_COLLECTIONS_BY_KEY } from "@cvx/jobs/precedents/collectionsShared";
import {
  CONTACT_SOURCE_FIELDS,
  CONTACT_SOURCE_KIND_LABELS,
  jobContactPartLabel,
  parseContactFieldKey,
  parseContactSourceFieldKey,
} from "@cvx/jobs/precedents/contactFields";
import {
  modifierDecoration,
  parseModifiers,
  serializeModifiers,
} from "./field-modifiers";
import { normalizeManualListMarkers } from "./manual-list-markers";

/**
 * Optional per-template context that lets `prepareHtmlForEditor` /
 * `refreshFieldChipLabels` rewrite generic `Job Contact:` chip labels to
 * the template's tagged role(s) — e.g. `Director: Name` when the
 * template is `cr:director` only, or `Director / Secured Creditor: Name`
 * for multi-tagged templates.
 *
 * Callers without a template context (variant snippets, AI chat) can
 * omit these and the chips fall back to the generic prefix.
 */
export interface RefreshLabelsContext {
  /** Rendered prefix substituted for `Job Contact` on `contact_job_*` chips. */
  jobContactPrefix?: string;
  /** Rendered prefix substituted for `Firm Contact` on `contact_firm_*` chips. */
  firmContactPrefix?: string;
}

/**
 * Convert stored templateHtml into TipTap-compatible HTML.
 *
 * Handles:
 * 1. Highlight-wrapped: `<span style="background:#FEF3C7">{{field_name}}</span>`
 * 2. Raw: `{{field_name}}`
 * 3. Divider divs: `<div style="height:2px;background:#1B3A5C;..."></div>` → `<hr>`
 *
 * Placeholders are converted to `<span data-type="field-placeholder" ...>` nodes.
 * Divider divs are converted to `<hr>` so TipTap's HorizontalRule can handle them
 * (TipTap strips empty divs otherwise).
 *
 * @param fieldsMap - Optional map of field key → label for display
 * @param context   - Optional per-template hints for chip label refresh.
 */
export function prepareHtmlForEditor(
  html: string,
  fieldsMap?: Record<string, string>,
  context?: RefreshLabelsContext,
): string {
  const toSpan = (key: string, modsRaw?: string) => {
    const label = fieldsMap?.[key] ?? formatKeyAsLabel(key);
    const mods = serializeModifiers(parseModifiers(modsRaw));
    const modsAttr = mods ? ` data-field-mods="${escapeAttr(mods)}"` : "";
    // `field-placeholder-chip` is required: the preview's chip styling in
    // `index.css` (base style + per-`data-field-source` colours) is keyed off
    // this class. Without it, a freshly-extracted template renders its
    // placeholders as plain unstyled text until an editor round-trip.
    return `<span data-type="field-placeholder" class="field-placeholder-chip" data-field-key="${escapeAttr(key)}" data-field-label="${escapeAttr(label)}"${modsAttr}>${escapeHtml(label)}</span>`;
  };

  const result = html
    // 1a. Divider divs → <hr> (height + background pattern)
    .replace(
      /<div[^>]*style="[^"]*height:\s*\d+px[^"]*background[^"]*"[^>]*>\s*<\/div>/gi,
      "<hr>",
    )
    // 1b. Empty divs/paragraphs with only a border → <hr>
    .replace(
      /<(?:div|p)[^>]*style="[^"]*border-(?:top|bottom):[^"]*"[^>]*>\s*<\/(?:div|p)>/gi,
      "<hr>",
    )
    // 2. Highlight-wrapped placeholders from extraction. Modifier names can
    // contain underscores (date_weekday, date_dmy_slash, …), so the suffix
    // charset must be [a-z0-9_] — a bare [a-z]+ silently fails to match any
    // date-formatted chip, leaving the raw token in the editor.
    .replace(
      /<span[^>]*style="[^"]*background:\s*#FEF3C7[^"]*"[^>]*>\{\{(\w+)((?:\|[a-z0-9_]+)*)\}\}<\/span>/gi,
      (_match, key: string, mods: string) => toSpan(key, mods),
    )
    // 3. Raw {{field_name|mod1|mod2}} placeholders
    .replace(
      /\{\{(\w+)((?:\|[a-z0-9_]+)*)\}\}/g,
      (_match, key: string, mods: string) => toSpan(key, mods),
    );

  // 4. Rewrite chip labels using collection-aware defaults so old
  //    templates whose `fieldsMap` still has the legacy "Item Name"
  //    entries surface the new "Director: Name" format everywhere the
  //    preview and editor consume this HTML (MOR — July 2026).
  // 5. Snap manual "(iii)&nbsp;&nbsp;…" list markers in hanging-indent
  //    paragraphs into fixed-width boxes so justification can't stretch
  //    the gap out of alignment (MOR-977). The ListMarker node round-trips
  //    the boxes, so a save bakes the fix into the stored HTML too.
  return normalizeManualListMarkers(refreshFieldChipLabels(result, context));
}

/**
 * Rewrite the text content + `data-field-label` of every
 * `<span data-type="field-placeholder">` chip so the label reflects the
 * enclosing collection context. Runs in the browser via DOMParser.
 *
 * Three chip families get a fresh label:
 *  - `item_<part>` inside a `<div data-type="repeat-block">` →
 *    `${Singular}: ${FieldLabel}` (looks up the block's `data-collection`).
 *  - `contact_<domain>_<part>` → `Job Contact: <Part>` /
 *    `Firm Contact: <Part>`.
 *
 * Any chip whose key we don't recognise (custom job/org fields,
 * source-scoped contact chips whose label already carries context, etc.)
 * is left untouched so hand-authored labels survive.
 *
 * Exported for tests + preview surfaces that render stored HTML without
 * going through the TipTap editor.
 */
export function refreshFieldChipLabels(
  html: string,
  context?: RefreshLabelsContext,
): string {
  if (typeof DOMParser === "undefined") return html;
  if (!html.includes('data-type="field-placeholder"')) return html;

  const doc = new DOMParser().parseFromString(
    `<div id="__morgan_root__">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("__morgan_root__");
  if (!root) return html;

  const chips = root.querySelectorAll<HTMLSpanElement>(
    'span[data-type="field-placeholder"]',
  );
  for (const chip of chips) {
    const key = chip.getAttribute("data-field-key");
    if (!key) continue;
    const fresh = deriveChipLabel(chip, key, context);
    if (!fresh) continue;
    chip.setAttribute("data-field-label", fresh);
    // Only rewrite the text when the chip's current text is the raw
    // label (no modifier decoration, no child elements) — preserves the
    // wrap/badge decoration painted by `decorateFieldChipModifiers`.
    if (chip.children.length === 0) {
      chip.textContent = fresh;
    }
  }

  return root.innerHTML;
}

/**
 * Compute the label a chip should display given its DOM context. Returns
 * `null` when we don't have enough context (unknown key family, missing
 * ancestor collection) so callers fall back to the existing label.
 */
function deriveChipLabel(
  chip: HTMLElement,
  key: string,
  context: RefreshLabelsContext | undefined,
): string | null {
  if (key.startsWith("item_")) {
    const repeatBlock = chip.closest<HTMLElement>('[data-type="repeat-block"]');
    const collectionKey = repeatBlock?.getAttribute("data-collection");
    if (!collectionKey) return null;
    const meta = TEMPLATE_COLLECTIONS_BY_KEY.get(collectionKey);
    if (!meta) return null;
    const part = key.slice("item_".length);
    const fieldMeta = meta.fields.find((f) => f.part === part);
    const fieldLabel = fieldMeta?.label ?? formatKeyAsLabel(part);
    return `${singulariseCollection(meta.label)}: ${fieldLabel}`;
  }

  // Source-scoped contact chip (checked before the basic parser
  // because both share the `contact_<domain>_` prefix).
  const source = parseContactSourceFieldKey(key);
  if (source) {
    const prefix =
      source.domain === "firm"
        ? (context?.firmContactPrefix ?? "Firm Contact")
        : (context?.jobContactPrefix ?? "Job Contact");
    const sourceLabel = CONTACT_SOURCE_KIND_LABELS[source.sourceKind];
    const fieldMeta = CONTACT_SOURCE_FIELDS[source.sourceKind].find(
      (f) => f.part === source.part,
    );
    const fieldLabel = fieldMeta?.label ?? formatKeyAsLabel(source.part);
    return `${prefix}: ${sourceLabel}: ${fieldLabel}`;
  }

  const basic = parseContactFieldKey(key);
  if (basic) {
    const prefix =
      basic.domain === "firm"
        ? (context?.firmContactPrefix ?? "Firm Contact")
        : (context?.jobContactPrefix ?? "Job Contact");
    return `${prefix}: ${jobContactPartLabel(basic.part, prefix)}`;
  }

  return null;
}

/** Keep in sync with the sibling helper in `field-placeholder-extension.ts`. */
function singulariseCollection(noun: string): string {
  if (noun.endsWith("ies")) return `${noun.slice(0, -3)}y`;
  if (noun.endsWith("s")) return noun.slice(0, -1);
  return noun;
}

/**
 * Convert TipTap editor HTML back to the canonical stored format.
 *
 * Replaces a field-placeholder `<span>` with `{{key}}`. The attribute order
 * must NOT be assumed: TipTap's `mergeAttributes` emits `data-field-key`
 * before `data-type`, so a regex that requires `data-type` first silently
 * fails to match — leaving bloated editor markup in the stored HTML. We match
 * the span by its `data-type` marker, then pull `data-field-key` out of the
 * captured attribute list regardless of where it sits.
 *
 * Also strips auto-generated `<colgroup><col style="min-width: …px"></colgroup>`
 * artifacts that TipTap's TableKit (with `resizable: true`) re-emits on every
 * serialization (see MOR-220, MOR-425). They confuse Word's altChunk import
 * (treated as a fixed 25px column width fighting `<table width:100%>`) and
 * the editor regenerates them on load anyway, so they don't need to live in
 * stored HTML. Colgroups whose `<col>` carries an explicit `width:` (a
 * user-resized column) are preserved.
 */
export function extractHtmlFromEditor(html: string): string {
  const result = html.replace(
    /<span\b([^>]*\bdata-type="field-placeholder"[^>]*)>[^<]*<\/span>/gi,
    (match, attrs: string) => {
      const keyMatch = /\bdata-field-key="([^"]+)"/i.exec(attrs);
      if (!keyMatch) return match;
      const modsMatch = /\bdata-field-mods="([^"]+)"/i.exec(attrs);
      const mods = modsMatch
        ? serializeModifiers(parseModifiers(modsMatch[1]))
        : "";
      const modsSuffix = mods ? `|${mods}` : "";
      return `{{${keyMatch[1]}${modsSuffix}}}`;
    },
  );
  return stripAutoGeneratedColgroups(result);
}

/**
 * Drop `<colgroup>` blocks whose every `<col>` carries `min-width: …px` and
 * no explicit `width`. Mirror of the same helper in
 * `src/utils/export/exportHtmlDocument.ts` — kept in this module so the
 * editor's save path doesn't have to import from the export pipeline.
 */
function stripAutoGeneratedColgroups(html: string): string {
  return html.replace(
    /<colgroup\b[^>]*>([\s\S]*?)<\/colgroup>/gi,
    (match, inner: string) => {
      const cols = [...inner.matchAll(/<col\b([^>]*?)\/?\s*>/gi)];
      if (cols.length === 0) return match;
      const allAutoMinWidth = cols.every((m) => {
        const colAttrs = m[1];
        const styleMatch = /\bstyle\s*=\s*"([^"]*)"/i.exec(colAttrs);
        const style = styleMatch?.[1] ?? "";
        const hasMinWidth = /(?:^|;)\s*min-width\s*:/i.test(style);
        const hasExplicitWidthStyle = /(?:^|;)\s*width\s*:/i.test(style);
        const hasExplicitWidthAttr = /\swidth\s*=/i.test(colAttrs);
        return hasMinWidth && !hasExplicitWidthStyle && !hasExplicitWidthAttr;
      });
      return allAutoMinWidth ? "" : match;
    },
  );
}

/**
 * Build a field key → label map from an extractedFields array.
 */
export function buildFieldsLabelMap(
  fields?: Array<{ key: string; label: string; canonicalKey?: string }>,
): Record<string, string> {
  if (!fields) return {};
  const map: Record<string, string> = {};
  for (const f of fields) {
    map[f.key] = f.label;
    if (f.canonicalKey) {
      map[f.canonicalKey] = f.label;
    }
  }
  return map;
}

/**
 * Field keys referenced in stored template HTML and/or extracted plain text.
 * Shapes must stay aligned with `replaceFieldPlaceholders` in day-one preview:
 * TipTap chips (`data-field-key`), AI highlight spans containing `{{key}}`,
 * and raw `{{key}}` tokens.
 *
 * `extractedContent` is a FALLBACK, consulted only when the body carries no
 * placeholders at all. It is the frozen plain-text artefact of the original
 * extraction and is never rewritten when the template body is edited, so
 * merging it into a body that does have placeholders resurrects every key the
 * document once used: a field the user deleted (or re-pointed at another key)
 * in the editor keeps surfacing as a "Needs Input" row in the generate wizard
 * and the day-one Edit Fields panel forever, with no way to clear it.
 */
export function collectPlaceholderKeysFromStoredTemplate(
  templateHtml: string,
  extractedContent?: string,
): Set<string> {
  // Group 1 captures the bare key in every pattern — modifiers (the optional
  // `|mod` suffix) must never leak into the collected key set. The modifier
  // charset must include underscores/digits: date-format modifiers are named
  // date_weekday, date_dmy_slash, etc., and a bare [a-z]+ fails to match the
  // whole token — the field then vanishes from the generate wizard's review
  // step (Shaw Gidley: ROCAP due date with 'Weekday, D Month YYYY').
  const patterns: RegExp[] = [
    /<span[^>]*data-field-key="(\w+)"[^>]*>/gi,
    /<span[^>]*style="[^"]*background:\s*#FEF3C7[^"]*"[^>]*>\{\{(\w+)(?:\|[a-z0-9_]+)*\}\}<\/span>/gi,
    /\{\{(\w+)(?:\|[a-z0-9_]+)*\}\}/g,
  ];

  const scan = (source: string): Set<string> => {
    const found = new Set<string>();
    if (!source.trim()) return found;
    for (const re of patterns) {
      re.lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = re.exec(source)) !== null) {
        // `item_*` keys belong to collection repeat blocks — they resolve
        // per-item at render time (collectionsShared.ts), not from the
        // scalar fieldValues map, so they must not surface as fillable
        // template fields.
        if (match[1].startsWith("item_")) continue;
        found.add(match[1]);
      }
    }
    return found;
  };

  const fromBody = scan(templateHtml);
  if (fromBody.size > 0) return fromBody;
  return scan(extractedContent ?? "");
}

/**
 * Strip variant-block wrappers down to the single option chosen for the job.
 *
 * Variant blocks render as:
 *   <div data-type="variant-block" data-group-slug="practitioner_count"
 *        data-snippet-refs='{"single":null,"multiple":"abc123"}'>
 *     <div data-variant-option="single">…option content…</div>
 *     <div data-variant-option="multiple">…option content…</div>
 *   </div>
 *
 * After this pass only the chosen option's inner HTML remains in place — the
 * wrapper and other options are removed. If the group has no selection, the
 * entire block is removed so unselected variants don't leak into the output.
 *
 * Pure transform — runs before {{field}} substitution so chips inside the
 * chosen variant get filled normally.
 *
 * Uses DOMParser instead of regex because option content can contain nested
 * divs / paragraphs / lists — regex matching of the nested closing tags is
 * unreliable.
 */
export function resolveVariantBlocks(
  html: string,
  selections?: Record<string, string>,
  options?: { highlight?: boolean },
): string {
  if (typeof DOMParser === "undefined") return html;
  if (!html.includes('data-type="variant-block"')) return html;

  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    "text/html",
  );
  const blocks = Array.from(
    doc.body.querySelectorAll<HTMLElement>('[data-type="variant-block"]'),
  );
  for (const block of blocks) {
    const groupSlug = block.getAttribute("data-group-slug") ?? "";
    const selected = groupSlug ? selections?.[groupSlug] : undefined;
    const options_ = Array.from(
      block.querySelectorAll<HTMLElement>(":scope > [data-variant-option]"),
    );
    if (options_.length === 0) {
      block.remove();
      continue;
    }
    const chosen = options_.find(
      (o) => o.getAttribute("data-variant-option") === selected,
    );
    if (!chosen) {
      block.remove();
      continue;
    }
    if (options?.highlight) {
      const wrapper = doc.createElement("div");
      wrapper.setAttribute("data-variant-highlight", groupSlug);
      while (chosen.firstChild) wrapper.appendChild(chosen.firstChild);
      block.replaceWith(wrapper);
    } else {
      const fragment = doc.createDocumentFragment();
      while (chosen.firstChild) fragment.appendChild(chosen.firstChild);
      block.replaceWith(fragment);
    }
  }
  return doc.body.innerHTML;
}

/** One variant-block placeholder instance parsed from stored HTML. */
export interface VariantBlockInstance {
  vbId: string | null;
  groupSlug: string;
  snippetRefs: Record<string, string | null>;
}

/**
 * Enumerate every variant-block placeholder in `html` in document order.
 * Used by the day-one picker to show one preview row per block — a group may
 * appear multiple times (e.g. a "Bankrupt Estate sentence" block and an
 * "Attachment sentence" block share a group slug), so callers cannot key off
 * the group alone.
 */
export function extractVariantBlockInstances(
  html: string | null | undefined,
): VariantBlockInstance[] {
  if (!html || typeof DOMParser === "undefined") return [];
  if (!html.includes('data-type="variant-block"')) return [];
  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    "text/html",
  );
  const blocks = Array.from(
    doc.body.querySelectorAll<HTMLElement>('[data-type="variant-block"]'),
  );
  return blocks.map((block) => {
    const raw = block.getAttribute("data-snippet-refs");
    const refs: Record<string, string | null> = raw
      ? (() => {
          try {
            return (JSON.parse(raw) as Record<string, string | null>) ?? {};
          } catch {
            return {};
          }
        })()
      : {};
    return {
      vbId: block.getAttribute("data-vb-id"),
      groupSlug: block.getAttribute("data-group-slug") ?? "",
      snippetRefs: refs,
    };
  });
}

/**
 * Inline elements that carry text formatting. A `background` /
 * `background-color` on one of these is a highlighter mark — Word and Google
 * Docs serialise their highlight colour as an inline `background-color` on a
 * `<span>` when you copy text out of them. Structural fills (table cells) live
 * on `<td>`/`<th>`, which this list deliberately excludes so they survive.
 */
const INLINE_HIGHLIGHT_TAGS =
  "span|mark|font|a|em|strong|b|i|u|s|sub|sup|small";

const INLINE_HIGHLIGHT_OPEN_TAG_RE = new RegExp(
  `<(?:${INLINE_HIGHLIGHT_TAGS})\\b[^>]*>`,
  "gi",
);

/**
 * The extraction pipeline's amber placeholder decoration (see
 * `PLACEHOLDER_SPAN_PREFIX` in `convex/settings/precedents/hybridDocxExtraction.ts`
 * and the AI extraction prompt in `convex/settings/precedents/actions.ts`):
 * `background:#FEF3C7;color:#92400E;padding:2px 4px;border-radius:3px;font-weight:500`.
 * The substitution regexes only unwrap this span when its content is exactly
 * one `{{token}}`; AI output doesn't always honour that shape, so the span can
 * survive into the render with a merged value inside it. Stripping only the
 * background then leaves the value brown, semi-bold and padded (MOR-1032) —
 * so when the amber background identifies a span as placeholder markup, its
 * companion decoration must go with it.
 */
const AMBER_PLACEHOLDER_BG_RE =
  /^background(?:-color)?\s*:\s*(?:#FEF3C7|rgb\(\s*254\s*,\s*243\s*,\s*199\s*\))\s*$/i;
const PLACEHOLDER_DECORATION_RE =
  /^(?:color\s*:\s*(?:#92400E|rgb\(\s*146\s*,\s*64\s*,\s*14\s*\))|font-weight\s*:\s*500|padding\s*:\s*2px\s+4px|border-radius\s*:\s*3px)\s*$/i;

/**
 * A span that carries the placeholder text colour together with the chip's
 * padding + border-radius is an extraction remnant even when its amber
 * background was already stripped by an earlier pass (snippet saves run this
 * strip before storing). All three together never occur as intentional
 * letter styling.
 */
function isOrphanedPlaceholderDecoration(decls: string[]): boolean {
  const has = (re: RegExp) => decls.some((d) => re.test(d));
  return (
    has(/^color\s*:\s*(?:#92400E|rgb\(\s*146\s*,\s*64\s*,\s*14\s*\))\s*$/i) &&
    has(/^padding\s*:\s*2px\s+4px\s*$/i) &&
    has(/^border-radius\s*:\s*3px\s*$/i)
  );
}

/**
 * Strip pasted highlighter backgrounds from inline text.
 *
 * Pasting from Word / Google Docs carries the source document's highlight
 * colour in as an inline `background-color` on a `<span>` (people commonly
 * highlight merge-field placeholders in green/yellow in their template docs).
 * Because it's an inline style, not a CSS class, that colour survives every
 * downstream render — including the PDF / Word export, which only loads the
 * print stylesheet. A formal letter never wants highlighter marks, so we remove
 * `background` / `background-color` declarations from inline text elements at
 * render time and on variant-snippet save. Table cell fills (`<td>`/`<th>`) are
 * left untouched.
 *
 * Spans carrying the extraction pipeline's amber placeholder decoration lose
 * the whole decoration (colour, weight, padding, radius), not just the
 * background — otherwise a merged value renders brown and semi-bold when the
 * decorated span survives token substitution (MOR-1032).
 *
 * Pure string transform (no DOM) so it runs identically in the browser, the
 * export iframe, and Node test/SSR contexts.
 */
export function stripInlineHighlights(html: string): string {
  if (
    !html ||
    !/background|#92400E|rgb\(\s*146\s*,\s*64\s*,\s*14\s*\)/i.test(html)
  ) {
    return html;
  }
  return html.replace(INLINE_HIGHLIGHT_OPEN_TAG_RE, (tag) =>
    tag.replace(/\sstyle="([^"]*)"/i, (_full, style: string) => {
      const decls = style
        .split(";")
        .map((decl) => decl.trim())
        .filter(Boolean);
      const isPlaceholderSpan =
        decls.some((d) => AMBER_PLACEHOLDER_BG_RE.test(d)) ||
        isOrphanedPlaceholderDecoration(decls);
      const cleaned = decls
        .filter((decl) => !/^background(-color)?\s*:/i.test(decl))
        .filter(
          (decl) => !isPlaceholderSpan || !PLACEHOLDER_DECORATION_RE.test(decl),
        )
        .join("; ");
      return cleaned ? ` style="${cleaned}"` : "";
    }),
  );
}

/**
 * Bake field-modifier decoration into static chip HTML.
 *
 * The editor's NodeView draws modifiers live (wrap chars + badges), but a saved
 * template renders its chips as plain `<span data-field-key … data-field-mods>`
 * whose visible text is just the label — so the preview shows the bare label
 * unless we apply the same decoration here. For each chip carrying
 * `data-field-mods` (and not a checkbox chip), this wraps the label and appends
 * muted badge spans, mirroring the NodeView. The `data-field-mods` attribute is
 * removed afterwards so the pass is idempotent.
 *
 * Used at preview/export time on the stored edited HTML — never on the value
 * substitution path, where modifiers already apply to the resolved value.
 */
export function decorateFieldChipModifiers(html: string): string {
  if (typeof DOMParser === "undefined") return html;
  if (!html.includes("data-field-mods")) return html;

  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    "text/html",
  );
  const chips = Array.from(
    doc.body.querySelectorAll<HTMLElement>("[data-field-mods]"),
  );
  for (const chip of chips) {
    const mode = chip.getAttribute("data-checkbox-mode");
    if (mode === "checked" || mode === "unchecked") {
      chip.removeAttribute("data-field-mods");
      continue;
    }
    const mods = parseModifiers(chip.getAttribute("data-field-mods"));
    chip.removeAttribute("data-field-mods");
    if (!mods.length) continue;

    const baseLabel = (chip.textContent ?? "").trim();
    const { open, close, badges } = modifierDecoration(mods);
    chip.textContent = `${open}${baseLabel}${close}`;
    for (const text of badges) {
      const badge = doc.createElement("span");
      badge.textContent = text;
      badge.setAttribute(
        "style",
        "opacity:0.6;font-size:0.75em;margin-left:3px",
      );
      chip.appendChild(badge);
    }
  }
  return doc.body.innerHTML;
}

/** A reusable variant snippet from the org-wide snippet library. */
export interface VariantSnippetForFill {
  _id: string;
  groupSlug: string;
  optionValue: string;
  contentHtml: string;
}

/**
 * Fill variant-option content from the snippet library.
 *
 * When a template author inserts a variant block, every option is seeded with
 * an `Edit this for "X"` placeholder. Picking a library snippet for an option
 * bakes its content in; options the author never touched keep the placeholder.
 * This pass swaps any still-placeholder option for the matching snippet's
 * content so the snippet library actually drives the rendered text — without
 * it, selecting a never-filled variant at job time renders the raw
 * `Edit this for "X"` placeholder.
 *
 * Resolution per block:
 *  1. Per-block override — a `snippetSelections` entry keyed by the block's
 *     `data-vb-id` (preferred) or, for legacy data, its `data-group-slug`.
 *     The override snippet is written into the SELECTED option's div (the one
 *     matching `variantSelections[groupSlug]`) so the override survives the
 *     later resolveVariantBlocks strip. Needs `variantSelections` to know
 *     which option is selected; when absent the override is skipped here.
 *  2. Otherwise, fill each still-unfilled option (first match wins):
 *     a. the option's own `data-snippet-id`
 *     b. the block's `data-snippet-refs` entry for that option value
 *     c. the sole / first library snippet for that (groupSlug, optionValue) pair
 *
 * Options with author-baked or hand-edited (non-placeholder) content are left
 * untouched — a per-template copy always wins over the library, so editing a
 * variant inside a template never gets clobbered. Call before
 * resolveVariantBlocks so field substitution sees the filled content.
 */
export function fillVariantSnippetPlaceholders(
  html: string,
  snippets: VariantSnippetForFill[],
  snippetSelections?: Record<string, string>,
  variantSelections?: Record<string, string>,
): string {
  if (typeof DOMParser === "undefined") return html;
  if (!html.includes('data-type="variant-block"')) return html;
  if (snippets.length === 0) return html;

  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    "text/html",
  );
  const blocks = Array.from(
    doc.body.querySelectorAll<HTMLElement>('[data-type="variant-block"]'),
  );
  for (const block of blocks) {
    const groupSlug = block.getAttribute("data-group-slug") ?? "";
    if (!groupSlug) continue;
    const vbId = block.getAttribute("data-vb-id");

    const rawRefs = block.getAttribute("data-snippet-refs");
    const refs: Record<string, string | null> = rawRefs
      ? (() => {
          try {
            return (JSON.parse(rawRefs) as Record<string, string | null>) ?? {};
          } catch {
            return {};
          }
        })()
      : {};

    const options = Array.from(
      block.querySelectorAll<HTMLElement>(":scope > [data-variant-option]"),
    );

    // 1) Per-block override wins — write the chosen snippet into the selected
    // option div so resolveVariantBlocks emits it.
    const overrideId =
      (vbId ? snippetSelections?.[vbId] : undefined) ??
      snippetSelections?.[groupSlug];
    const selectedOption = variantSelections?.[groupSlug];
    if (overrideId && selectedOption) {
      const snippet = snippets.find((s) => s._id === overrideId);
      const target = options.find(
        (o) => o.getAttribute("data-variant-option") === selectedOption,
      );
      if (snippet && target) {
        target.innerHTML = wrapInlineHtml(snippet.contentHtml);
        continue; // override applied; skip default fill for this block
      }
    }

    // 2) Default fill — only options the author never customised. A baked
    // snippet or a hand-edited copy must never be clobbered by the library.
    for (const option of options) {
      if (!isUnfilledVariantOption(option)) continue;

      const optionValue = option.getAttribute("data-variant-option") ?? "";
      const snippetId =
        option.getAttribute("data-snippet-id") ?? refs[optionValue] ?? null;

      const snippet = snippetId
        ? snippets.find((s) => s._id === snippetId)
        : snippets.find(
            (s) => s.groupSlug === groupSlug && s.optionValue === optionValue,
          );
      if (!snippet) continue;

      option.innerHTML = wrapInlineHtml(snippet.contentHtml);
    }
  }
  return doc.body.innerHTML;
}

/**
 * True when a variant option still holds the inserted seed placeholder
 * (`Edit this for "X"`) or is empty — i.e. the author never gave it content.
 * Matches the same `Edit this for` detection the variantBlock NodeView uses.
 */
function isUnfilledVariantOption(option: HTMLElement): boolean {
  const text = (option.textContent ?? "").trim();
  return text === "" || text.startsWith("Edit this for");
}

/** Wrap inline-only snippet HTML in a paragraph so it nests in a block slot. */
function wrapInlineHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return "<p></p>";
  return /^<(p|div|h[1-6]|ul|ol|blockquote|pre|table|hr)\b/i.test(trimmed)
    ? trimmed
    : `<p>${trimmed}</p>`;
}

function formatKeyAsLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (c) => map[c]);
}

function escapeAttr(text: string): string {
  return text.replace(/"/g, "&quot;").replace(/&/g, "&amp;");
}
